import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

type DashboardData = {
  meta: { mode: string; generatedAt: string; campaign: string; source: string };
  metrics: Record<string, number>;
  hourly: Array<{ hour: string; calls: number; contacts: number; sales: number }>;
  agents: Array<{ name: string; status: string; calls: number; customerMinutes: number; pausePct: number; contactRate: number }>;
  priority99: { queueDepth: number; oldestAgeSeconds: number; accepted: number; rejected: number; medianSpeedSeconds: number; contactRate: number; saleRate: number };
  dispositions: Array<{ code: string; name: string; selectedBy: string; category: string; agentSelectable: boolean; calls: number; issue?: string }>;
  reports: Array<{ name: string; category: string; purpose: string; kpis: string; access: string; applicable: boolean }>;
  issues: Array<{ severity: string; title: string; detail: string }>;
};

type Page = "overview" | "live" | "priority" | "agents" | "reports" | "dispositions" | "compliance";

const nav: Array<{ id: Page; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "live", label: "Live Operations" },
  { id: "priority", label: "Priority 99" },
  { id: "agents", label: "Agents" },
  { id: "reports", label: "Report Catalogue" },
  { id: "dispositions", label: "Dispositions" },
  { id: "compliance", label: "Controls" },
];

const formatInt = (value: number) => new Intl.NumberFormat("en-ZA").format(value);
const formatPct = (value: number) => `${value.toFixed(1)}%`;
const statusClass = (status: string) => `status status-${status.toLowerCase()}`;

function App() {
  const [page, setPage] = useState<Page>("overview");
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("2026-07-22");
  const [to, setTo] = useState("2026-07-22");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetch(`/api/dashboard?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`API returned ${response.status}`);
        return (await response.json()) as DashboardData;
      })
      .then((payload) => {
        setData(payload);
        setError("");
      })
      .catch((requestError: Error) => {
        if (requestError.name !== "AbortError") setError(requestError.message);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [from, to]);

  const content = useMemo(() => {
    if (!data) return null;
    switch (page) {
      case "live": return <LiveOperations data={data} />;
      case "priority": return <Priority99 data={data} />;
      case "agents": return <Agents data={data} />;
      case "reports": return <Reports data={data} />;
      case "dispositions": return <Dispositions data={data} />;
      case "compliance": return <Compliance data={data} />;
      default: return <Overview data={data} />;
    }
  }, [page, data]);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">O</div>
          <div><strong>Ontact</strong><span>Dialler Intelligence</span></div>
        </div>
        <nav>{nav.map((item) => <button key={item.id} className={page === item.id ? "active" : ""} onClick={() => setPage(item.id)}>{item.label}</button>)}</nav>
        <div className="sidebar-note"><span className="pulse" /> Read-only operational mirror</div>
      </aside>

      <main>
        <header className="topbar">
          <div>
            <p className="eyebrow">Outbound / OfferShop</p>
            <h1>{nav.find((item) => item.id === page)?.label}</h1>
          </div>
          <div className="filters">
            <label>From<input type="date" value={from} onChange={(event: { target: { value: string } }) => setFrom(event.target.value)} /></label>
            <label>To<input type="date" value={to} onChange={(event: { target: { value: string } }) => setTo(event.target.value)} /></label>
            <span className="source-chip">{data?.meta.mode ?? "Loading"}</span>
          </div>
        </header>

        {error && <div className="error-banner">Unable to load the API: {error}</div>}
        {loading && !data ? <div className="loading">Loading dialler intelligence…</div> : content}
        <footer>Generated {data ? new Date(data.meta.generatedAt).toLocaleString("en-ZA") : "—"} · Source: {data?.meta.source ?? "—"}</footer>
      </main>
    </div>
  );
}

function Overview({ data }: { data: DashboardData }) {
  const m = data.metrics;
  return <div className="page-stack">
    <section className="kpi-grid">
      <Kpi label="Calls" value={formatInt(m.totalCalls)} note="Outbound attempts" />
      <Kpi label="Human answers" value={formatInt(m.humanAnswers)} note={formatPct(m.humanAnswerRate)} />
      <Kpi label="Contacts" value={formatInt(m.contacts)} note={formatPct(m.contactRate)} />
      <Kpi label="Sales" value={formatInt(m.sales)} note={`${formatPct(m.saleRate)} per attempt`} />
      <Kpi label="Agent login" value={`${m.loginHours.toFixed(1)}h`} note={`${formatPct(m.waitPct)} wait`} />
      <Kpi label="P99 median speed" value={`${m.p99MedianSpeed}s`} note={`${data.priority99.queueDepth} waiting`} accent />
    </section>
    <section className="two-column">
      <Panel title="Hourly calling pattern" subtitle="Calls, contacts and sales by operating interval"><MiniChart data={data.hourly} /></Panel>
      <Panel title="Operational attention" subtitle="Configuration and data-quality issues"><IssueList issues={data.issues.slice(0, 5)} /></Panel>
    </section>
    <section className="two-column">
      <Panel title="Priority 99 health" subtitle="Next-available hot-lead queue"><PrioritySummary priority={data.priority99} /></Panel>
      <Panel title="Top disposition volume" subtitle="Current outcome mix"><DispositionBars dispositions={data.dispositions.slice(0, 7)} /></Panel>
    </section>
  </div>;
}

function LiveOperations({ data }: { data: DashboardData }) {
  return <div className="page-stack">
    <section className="kpi-grid compact">
      <Kpi label="Agents visible" value={String(data.agents.length)} note="Outbound campaign" />
      <Kpi label="Ready" value={String(data.agents.filter((a) => a.status === "READY").length)} note="Available now" />
      <Kpi label="In call" value={String(data.agents.filter((a) => a.status === "INCALL").length)} note="Customer-connected" />
      <Kpi label="P99 queue" value={String(data.priority99.queueDepth)} note={`${data.priority99.oldestAgeSeconds}s oldest`} accent />
    </section>
    <Panel title="Agent state board" subtitle="Designed to align Real-time Report and User Status into one operational view">
      <table><thead><tr><th>Agent</th><th>Status</th><th>Calls</th><th>Customer time</th><th>Pause</th><th>Contact rate</th></tr></thead>
        <tbody>{data.agents.map((agent) => <tr key={agent.name}><td><strong>{agent.name}</strong></td><td><span className={statusClass(agent.status)}>{agent.status}</span></td><td>{agent.calls}</td><td>{agent.customerMinutes} min</td><td>{formatPct(agent.pausePct)}</td><td>{formatPct(agent.contactRate)}</td></tr>)}</tbody>
      </table>
    </Panel>
  </div>;
}

function Priority99({ data }: { data: DashboardData }) {
  const p = data.priority99;
  return <div className="page-stack">
    <section className="kpi-grid compact">
      <Kpi label="Queue depth" value={String(p.queueDepth)} note="Target ≤ 12" accent />
      <Kpi label="Oldest lead" value={`${p.oldestAgeSeconds}s`} note="Critical at 120s" />
      <Kpi label="Accepted" value={formatInt(p.accepted)} note="API intake" />
      <Kpi label="Rejected" value={formatInt(p.rejected)} note="DNC / duplicate / time" />
      <Kpi label="Median speed" value={`${p.medianSpeedSeconds}s`} note="Hopper to dial" />
      <Kpi label="P99 sale rate" value={formatPct(p.saleRate)} note={`${formatPct(p.contactRate)} contact`} />
    </section>
    <section className="two-column">
      <Panel title="Priority 99 operating model" subtitle="Manual campaign: next available, not predictive interruption">
        <ol className="flow-list"><li>Validate DNC, call time, attempts and duplicates</li><li>Add or update the VICIdial lead</li><li>Insert into hopper with priority 99</li><li>Present before normal leads when an agent clicks Dial Next Number</li><li>Record speed-to-lead and final outcome</li></ol>
      </Panel>
      <Panel title="Pilot controls" subtitle="Launch gates for Ontact">
        <div className="control-grid"><Control label="Queue warning" value="8 leads" /><Control label="Queue critical" value="12 leads" /><Control label="Age warning" value="60 sec" /><Control label="Age critical" value="120 sec" /><Control label="Caller ID" value="087 085 2677" /><Control label="Initial mode" value="MANUAL" /></div>
      </Panel>
    </section>
  </div>;
}

function Agents({ data }: { data: DashboardData }) {
  return <div className="page-stack"><Panel title="Agent performance" subtitle="Combines Agent Performance Detail, Agent Time Detail, User Stats and time-sheet signals">
    <div className="agent-cards">{data.agents.map((agent) => <article key={agent.name} className="agent-card"><div><strong>{agent.name}</strong><span className={statusClass(agent.status)}>{agent.status}</span></div><dl><dt>Calls</dt><dd>{agent.calls}</dd><dt>Customer minutes</dt><dd>{agent.customerMinutes}</dd><dt>Pause</dt><dd>{formatPct(agent.pausePct)}</dd><dt>Contact rate</dt><dd>{formatPct(agent.contactRate)}</dd></dl></article>)}</div>
  </Panel></div>;
}

function Reports({ data }: { data: DashboardData }) {
  return <div className="page-stack"><Panel title="VICIdial report catalogue" subtitle="Functional requirements extracted from the supplied workbook; inbound reports are retained as not applicable">
    <div className="report-list">{data.reports.map((report) => <article key={report.name} className={!report.applicable ? "muted" : ""}><div><span className="report-category">{report.category}</span><strong>{report.name}</strong></div><p>{report.purpose}</p><small><b>KPIs:</b> {report.kpis}</small><small><b>Access:</b> {report.access}</small>{!report.applicable && <span className="not-applicable">Not applicable: no inbound operation</span>}</article>)}</div>
  </Panel></div>;
}

function Dispositions({ data }: { data: DashboardData }) {
  return <div className="page-stack"><Panel title="Disposition governance" subtitle="One controlled register for campaign, system and workflow statuses">
    <table><thead><tr><th>Code</th><th>Name</th><th>Selected by</th><th>Category</th><th>Agent screen</th><th>Calls</th><th>Issue</th></tr></thead><tbody>{data.dispositions.map((d) => <tr key={d.code}><td><code>{d.code}</code></td><td>{d.name}</td><td>{d.selectedBy}</td><td>{d.category}</td><td>{d.agentSelectable ? "Yes" : "No"}</td><td>{d.calls}</td><td className={d.issue ? "issue-cell" : ""}>{d.issue ?? "—"}</td></tr>)}</tbody></table>
  </Panel></div>;
}

function Compliance({ data }: { data: DashboardData }) {
  return <div className="page-stack"><section className="two-column"><Panel title="Current control gaps" subtitle="Read-only mirror of high-priority dialler risks"><IssueList issues={data.issues} /></Panel><Panel title="Required Priority 99 gates" subtitle="No lead should bypass these checks"><div className="check-list">{["Internal DNC check", "Campaign DNC check", "Approved calling window", "Daily and rolling attempt limit", "Duplicate and idempotency check", "Existing callback check", "Existing live or hopper record check", "Single Truecaller-verified CLI", "Recording and outcome trace"].map((item) => <div key={item}><span>✓</span>{item}</div>)}</div></Panel></section></div>;
}

function Kpi({ label, value, note, accent = false }: { label: string; value: string; note: string; accent?: boolean }) { return <article className={`kpi ${accent ? "kpi-accent" : ""}`}><span>{label}</span><strong>{value}</strong><small>{note}</small></article>; }
function Panel({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) { return <section className="panel"><div className="panel-head"><div><h2>{title}</h2><p>{subtitle}</p></div></div>{children}</section>; }
function Control({ label, value }: { label: string; value: string }) { return <div className="control"><span>{label}</span><strong>{value}</strong></div>; }
function IssueList({ issues }: { issues: DashboardData["issues"] }) { return <div className="issue-list">{issues.map((issue) => <article key={issue.title}><span className={`severity severity-${issue.severity.toLowerCase()}`}>{issue.severity}</span><div><strong>{issue.title}</strong><p>{issue.detail}</p></div></article>)}</div>; }
function PrioritySummary({ priority }: { priority: DashboardData["priority99"] }) { return <div className="priority-summary"><div className="ring"><strong>{priority.queueDepth}</strong><span>waiting</span></div><div><p><b>{priority.oldestAgeSeconds}s</b> oldest lead</p><p><b>{priority.medianSpeedSeconds}s</b> median speed-to-dial</p><p><b>{formatPct(priority.contactRate)}</b> contact rate</p><p><b>{formatPct(priority.saleRate)}</b> sale rate</p></div></div>; }
function DispositionBars({ dispositions }: { dispositions: DashboardData["dispositions"] }) { const max = Math.max(...dispositions.map((d) => d.calls), 1); return <div className="bars">{dispositions.map((d) => <div key={d.code}><span>{d.code}</span><i style={{ width: `${Math.max((d.calls / max) * 100, 2)}%` }} /><b>{d.calls}</b></div>)}</div>; }
function MiniChart({ data }: { data: DashboardData["hourly"] }) { const max = Math.max(...data.map((d) => d.calls), 1); return <div className="mini-chart">{data.map((d) => <div key={d.hour} className="mini-column"><div className="stack"><i className="calls" style={{ height: `${Math.max((d.calls / max) * 100, 4)}%` }} /><i className="contacts" style={{ height: `${Math.max((d.contacts / max) * 100, 2)}%` }} /></div><span>{d.hour}</span></div>)}</div>; }

export default App;
